import { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { getDashboardOrganizations, linkAdminStripeCustomer } from '@/services/adminDashboardService'
import type { DashboardOrganizationResponse, LinkCustomerResponse } from '@/types/adminDashboard'

interface LinkCustomerModalProps {
  isOpen: boolean
  onClose: () => void
  stripeCustomerId: string
  customerEmail?: string
  token: string
  onSuccess: (result: LinkCustomerResponse) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LinkCustomerModal({
  isOpen,
  onClose,
  stripeCustomerId,
  customerEmail,
  token,
  onSuccess,
}: LinkCustomerModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const [organizations, setOrganizations] = useState<DashboardOrganizationResponse[]>([])
  const [organizationsLoading, setOrganizationsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load ALL organizations on modal open.
  // The organizations endpoint filters by createdAt date range, so we pass a very wide range
  // (from year 2000 to now) and the maximum limit (100) to ensure all orgs are available
  // for client-side search — not just ones created in the last 30 days.
  useEffect(() => {
    if (!isOpen || !token) return
    setOrganizationsLoading(true)
    dispatch(
      getDashboardOrganizations(
        token,
        {
          startDate: '2000-01-01T00:00:00.000Z',
          endDate: new Date().toISOString(),
          limit: 100,
        },
        false,
      ),
    )
      .then((resp) => {
        if (resp.data) setOrganizations(resp.data)
      })
      .finally(() => setOrganizationsLoading(false))
  }, [isOpen, token, dispatch])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("")
      setSelectedOrg(null)
    }
  }, [isOpen])

  // Filter orgs by search term (client-side)
  const filteredOrgs = useMemo(() => {
    if (!searchTerm.trim()) return []
    return organizations.filter((org) =>
      org.organizationName.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [searchTerm, organizations])

  // Show dropdown only when typing and no org is selected yet
  const showDropdown = searchTerm.trim().length > 0 && filteredOrgs.length > 0 && !selectedOrg

  const handleSelectOrg = (org: DashboardOrganizationResponse) => {
    setSelectedOrg({ id: org.organizationId, name: org.organizationName })
    setSearchTerm(org.organizationName)
  }

  const handleLink = async () => {
    if (!selectedOrg || !token) return
    setIsSubmitting(true)
    const resp = await dispatch(
      linkAdminStripeCustomer(token, selectedOrg.id, {
        stripeCustomerId,
        setPrimary: true,
      }),
    )
    setIsSubmitting(false)
    if (resp.data) {
      onSuccess(resp.data)
    }
  }

  const canSubmit = !!selectedOrg && !isSubmitting

  const inputBaseClass = "w-full px-3 py-2 border border-slate-200 rounded-md text-[13px] text-slate-900 bg-white outline-none box-border"
  const inputDisabledClass = `${inputBaseClass} bg-slate-50 text-slate-500`

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        aria-label="Link Stripe Customer to Organization"
        className="block p-7 w-[460px] max-w-[95vw] rounded-lg shadow-2xl bg-white border border-slate-200 gap-0"
      >
        {/* Title */}
        <div className="text-base font-bold text-slate-900 mb-1">
          Link Stripe Customer to Organization
        </div>
        <div className="text-[13px] text-slate-500 mb-5">
          This will update Stripe metadata and reconcile existing invoices and subscriptions.
        </div>

        {/* Stripe Customer ID */}
        <div className="mb-3.5">
          <label htmlFor="stripe-customer-id" className="block text-xs font-medium text-slate-900 mb-1">Stripe Customer ID</label>
          <input id="stripe-customer-id" value={stripeCustomerId} disabled readOnly className={inputDisabledClass} />
          <div className="text-[11px] text-slate-400 mt-1">Read-only — pulled from Stripe</div>
        </div>

        {/* Customer email */}
        {customerEmail && (
          <div className="mb-3.5">
            <label htmlFor="stripe-customer-email" className="block text-xs font-medium text-slate-900 mb-1">Customer email</label>
            <input id="stripe-customer-email" value={customerEmail} disabled readOnly className={inputDisabledClass} />
          </div>
        )}

        {/* TCS Organization search */}
        <div className="mb-3.5">
          <label htmlFor="tcs-organization-search" className="block text-xs font-medium text-slate-900 mb-1">TCS Organization</label>
          <div className="relative">
            <input
              id="tcs-organization-search"
              placeholder="Search by organization name…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                // Clear selection if user edits the name after picking one
                if (selectedOrg && e.target.value !== selectedOrg.name) {
                  setSelectedOrg(null)
                }
              }}
              disabled={organizationsLoading}
              className={organizationsLoading ? inputDisabledClass : inputBaseClass}
            />
            {showDropdown && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 border border-slate-200 rounded-md bg-white shadow-md">
                {filteredOrgs.map((org) => (
                  <button
                    type="button"
                    key={org.organizationId}
                    onClick={() => handleSelectOrg(org)}
                    className="block w-full text-left px-3 py-[9px] text-[13px] text-slate-900 cursor-pointer border-none bg-transparent hover:bg-blue-50 hover:text-blue-600"
                  >
                    {org.organizationName}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {organizationsLoading ? 'Loading organizations…' : 'Start typing to search organizations'}
          </div>
        </div>

        {/* Success note — shown after org is selected */}
        {selectedOrg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5 text-xs text-emerald-800 mb-1.5">
            ✓ Organization selected. On save: Stripe metadata will be updated, and existing
            invoices + subscriptions for this customer will be linked to the organization.
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-3.5 py-[7px] rounded-md text-xs font-medium cursor-pointer bg-transparent border border-slate-200 text-slate-500 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={!canSubmit}
            className={`inline-flex items-center px-3.5 py-[7px] rounded-md text-xs font-medium text-white border-none ${
              canSubmit
                ? 'bg-blue-600 cursor-pointer hover:bg-blue-700'
                : 'bg-blue-300 cursor-not-allowed opacity-70'
            }`}
          >
            {isSubmitting ? 'Linking…' : 'Link Customer'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
